<template>
    <div>
        <HamletStaticTable
            :columns="columns"
            :items="localRows"
            :striped="false"
            :bordered="true"
            empty-mode="message"
            card-class="pt-0 mt-0"
            table-container-class="table-responsive hamlet-table substances-table"
        >
            <template #header-label>
                {{ $t('Component(ResearchReviewProcess):substances') }}
            </template>

            <template #header-controls>
                <CommonButton
                    variant="primary"
                    size="sm"
                    @click="addRow"
                >
                    {{ $t('Component(Button):add') }}
                </CommonButton>
            </template>

            <template #cell-match="{ item }">
                <span
                    class="match-dot"
                    :class="item.linkedSubstanceId ? 'match-dot-green' : 'match-dot-red'"
                />
            </template>

            <template #cell-substance="{ item }">
                <SubstanceSelect
                    :value="selectedSubstance(item)"
                    :append-to-body="true"
                    :clearable="true"
                    :placeholder="$t('Component(Composition):search-placeholder')"
                    :start-typing-text="$t('Component(Composition):search-label')"
                    :no-options-text="$t('Component(Text):nothing-found')"
                    @input="onSubstanceSelect(item, $event)"
                >
                    <template #selected-option="option">
                        <span
                            class="substance-selected-option"
                            :title="option.name || option.title || ''"
                        >
                            {{ option.name || option.title || '' }}
                        </span>
                    </template>
                </SubstanceSelect>
            </template>

            <template #cell-name="{ item }">
                <b-form-input v-model="item.name" />
            </template>

            <template #cell-ecNumber="{ item }">
                <b-form-input v-model="item.ecNumber" />
            </template>

            <template #cell-casNumber="{ item }">
                <b-form-input v-model="item.casNumber" />
            </template>

            <template #cell-operator="{ item }">
                <v-select
                    v-model="item.concentration.operator"
                    :options="operatorOptions"
                    label="label"
                    :reduce="option => option.id"
                    :append-to-body="true"
                    :clearable="false"
                />
            </template>

            <template #cell-value="{ item }">
                <b-form-input
                    v-model.number="item.concentration.value"
                    type="number"
                    step="any"
                />
            </template>

            <template #cell-unit="{ item }">
                <v-select
                    v-model="item.concentration.unit"
                    :options="unitOptions"
                    label="label"
                    :reduce="option => option.id"
                    :append-to-body="true"
                    :clearable="false"
                />
            </template>

            <template #cell-actions="{ item }">
                <CommonButton
                    variant="danger"
                    size="sm"
                    class="btn-icon"
                    @click="removeRow(item)"
                >
                    <feather-icon
                        icon="MinusIcon"
                        size="14"
                    />
                </CommonButton>
            </template>
        </HamletStaticTable>
    </div>
</template>

<script>
import { BFormInput } from 'bootstrap-vue'
import { HamletStaticTable, Button as CommonButton, SubstanceSelect } from '@core/components/common'
import SubstanceApi from '@core/api/request/substance'
import vSelect from 'vue-select'
import { v4 as uuidv4 } from 'uuid'

function clone(value) {
    return JSON.parse(JSON.stringify(value))
}

function isEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b)
}

function createConcentration(concentration = {}) {
    return {
        unit: concentration.unit || 'w/w',
        basis: concentration.basis || 'homogeneous_material',
        value: concentration.value !== undefined ? concentration.value : null,
        operator: concentration.operator || '<=',
    }
}

function resolveExtractedSubstanceId(item) {
    if (item && item.extractedSubstanceId !== undefined && item.extractedSubstanceId !== null && item.extractedSubstanceId !== '') {
        return item.extractedSubstanceId
    }

    if (item && item.rowKey) {
        return item.rowKey
    }

    return null
}

function createTemporarySubstanceKey() {
    return uuidv4()
}

function normalizeSubstances(items) {
    return Array.isArray(items)
        ? items.map(item => {
            const row = item && typeof item === 'object' ? clone(item) : {}

            return {
                extractedSubstanceId: row.extractedSubstanceId !== undefined ? row.extractedSubstanceId : null,
                name: row.name || '',
                ecNumber: row.ecNumber || '',
                casNumber: row.casNumber || '',
                concentration: createConcentration(row.concentration),
            }
        })
        : []
}

function normalizeLinks(items) {
    return Array.isArray(items)
        ? items.map(item => {
            const row = item && typeof item === 'object' ? clone(item) : {}

            return {
                extractedSubstanceId: row.extractedSubstanceId !== undefined ? row.extractedSubstanceId : null,
                substanceId: row.substanceId !== undefined && row.substanceId !== null ? row.substanceId : '',
                type: row.type || 'manual',
                status: row.status || 'accepted',
                confidence: row.confidence !== undefined ? row.confidence : null,
                reason: row.reason !== undefined ? row.reason : null,
            }
        })
        : []
}

function buildRows(substances, links) {
    const normalizedSubstances = normalizeSubstances(substances)
    const normalizedLinks = normalizeLinks(links)
    const rows = normalizedSubstances.map((item, index) => ({
        rowKey: item.extractedSubstanceId !== null && item.extractedSubstanceId !== undefined
            ? String(item.extractedSubstanceId)
            : `existing-substance-${index}`,
        extractedSubstanceId: item.extractedSubstanceId,
        linkedSubstanceId: '',
        linkType: 'manual',
        linkStatus: 'accepted',
        linkConfidence: null,
        linkReason: null,
        name: item.name,
        ecNumber: item.ecNumber,
        casNumber: item.casNumber,
        concentration: createConcentration(item.concentration),
    }))

    const rowsByExtractedId = rows.reduce((acc, row) => {
        if (row.extractedSubstanceId !== null && row.extractedSubstanceId !== undefined) {
            acc[String(row.extractedSubstanceId)] = row
        }

        return acc
    }, {})

    const rowsWithoutExtractedId = rows.filter(row => row.extractedSubstanceId === null || row.extractedSubstanceId === undefined)
    let nullExtractedRowIndex = 0

    normalizedLinks.forEach(link => {
        const key = link.extractedSubstanceId !== null && link.extractedSubstanceId !== undefined
            ? String(link.extractedSubstanceId)
            : null
        const rowIndex = nullExtractedRowIndex
        const existingRow = key
            ? rowsByExtractedId[key]
            : rowsWithoutExtractedId[rowIndex] || null

        if (!key) {
            nullExtractedRowIndex += 1
        }

        if (existingRow) {
            existingRow.linkedSubstanceId = link.substanceId
            existingRow.linkType = link.type
            existingRow.linkStatus = link.status
            existingRow.linkConfidence = link.confidence
            existingRow.linkReason = link.reason
            return
        }

        rows.push({
            rowKey: key || `extra-substance-${rowIndex}`,
            extractedSubstanceId: link.extractedSubstanceId,
            linkedSubstanceId: link.substanceId,
            linkType: link.type,
            linkStatus: link.status,
            linkConfidence: link.confidence,
            linkReason: link.reason,
            name: '',
            ecNumber: '',
            casNumber: '',
            concentration: createConcentration(),
        })
    })

    return rows
}

function serializeSubstances(rows) {
    return rows.map(item => ({
        extractedSubstanceId: resolveExtractedSubstanceId(item),
        name: item.name || '',
        ecNumber: item.ecNumber || '',
        casNumber: item.casNumber || '',
        concentration: createConcentration(item.concentration),
    }))
}

function serializeLinks(rows) {
    return rows
        .filter(item => item && item.linkedSubstanceId)
        .map(item => ({
            extractedSubstanceId: resolveExtractedSubstanceId(item),
            substanceId: item.linkedSubstanceId,
            type: item.linkType || 'manual',
            status: item.linkStatus || 'accepted',
            confidence: item.linkConfidence !== undefined ? item.linkConfidence : null,
            reason: item.linkReason !== undefined ? item.linkReason : null,
        }))
}

function emptyRow() {
    return {
        rowKey: '',
        extractedSubstanceId: null,
        linkedSubstanceId: '',
        linkType: 'manual',
        linkStatus: 'accepted',
        linkConfidence: null,
        linkReason: null,
        name: '',
        ecNumber: '',
        casNumber: '',
        concentration: createConcentration(),
    }
}

export default {
    name: 'SubstancesSection',
    components: {
        BFormInput,
        HamletStaticTable,
        CommonButton,
        SubstanceSelect,
        vSelect,
    },
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        links: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            localRows: buildRows(this.value, this.links),
            selectedSubstancesById: {},
            hydratingSubstanceIds: [],
            columns: [
                {
                    key: 'match', label: '', thClass: 'text-center substances-col-match', tdClass: 'text-center substances-col-match',
                },
                {
                    key: 'substance', label: this.$t('Component(ResearchReviewProcess):chemity-substance'), thClass: 'substances-col-substance', tdClass: 'substances-col-substance',
                },
                {
                    key: 'name', label: this.$t('Component(Grid):column-name'), thClass: 'substances-col-name', tdClass: 'substances-col-name',
                },
                {
                    key: 'ecNumber', label: this.$t('Component(SubstanceTable):column-ec'), thClass: 'substances-col-ec', tdClass: 'substances-col-ec',
                },
                {
                    key: 'casNumber', label: this.$t('Component(SubstanceTable):column-cas'), thClass: 'substances-col-cas', tdClass: 'substances-col-cas',
                },
                {
                    key: 'operator', label: this.$t('Component(Label):comparison-operator'), thClass: 'substances-col-operator', tdClass: 'substances-col-operator',
                },
                {
                    key: 'value', label: this.$t('Component(Label):value'), thClass: 'substances-col-value', tdClass: 'substances-col-value',
                },
                {
                    key: 'unit', label: this.$t('Component(Label):unit'), thClass: 'substances-col-unit', tdClass: 'substances-col-unit',
                },
                {
                    key: 'actions', label: '', thClass: 'text-right substances-col-actions', tdClass: 'text-right substances-col-actions',
                },
            ],
            operatorOptions: [
                { id: '<=', label: '<=' },
                { id: '<', label: '<' },
                { id: '=', label: '=' },
                { id: '>=', label: '>=' },
                { id: '>', label: '>' },
            ],
            unitOptions: [
                { id: 'w/w', label: 'w/w' },
                { id: 'mg/kg', label: 'mg/kg' },
                { id: '%', label: '%' },
            ],
        }
    },
    watch: {
        value: {
            deep: true,
            handler(newValue) {
                const rebuilt = buildRows(newValue, this.links)
                if (isEqual(rebuilt, this.localRows)) {
                    return
                }

                this.localRows = rebuilt
                this.hydrateLinkedSubstances()
            },
        },
        links: {
            deep: true,
            handler(newValue) {
                const rebuilt = buildRows(this.value, newValue)
                if (isEqual(rebuilt, this.localRows)) {
                    return
                }

                this.localRows = rebuilt
                this.hydrateLinkedSubstances()
            },
        },
        localRows: {
            deep: true,
            handler(newValue) {
                const emittedSubstances = serializeSubstances(newValue)
                const currentSubstances = normalizeSubstances(this.value)

                if (!isEqual(emittedSubstances, currentSubstances)) {
                    this.$emit('input', emittedSubstances)
                }

                const emittedLinks = serializeLinks(newValue)
                const currentLinks = normalizeLinks(this.links)

                if (!isEqual(emittedLinks, currentLinks)) {
                    this.$emit('update:links', emittedLinks)
                }
            },
        },
    },
    created() {
        this.hydrateLinkedSubstances()
    },
    methods: {
        addRow() {
            this.localRows.push({
                ...emptyRow(),
                rowKey: createTemporarySubstanceKey(),
            })
        },
        removeRow(item) {
            const index = this.localRows.findIndex(row => row.rowKey === item.rowKey)
            if (index === -1) {
                return
            }

            this.localRows.splice(index, 1)
        },
        selectedSubstance(item) {
            if (!item || !item.linkedSubstanceId) {
                return null
            }

            return this.selectedSubstancesById[item.linkedSubstanceId] || null
        },
        onSubstanceSelect(item, selected) {
            const index = this.localRows.findIndex(row => row.rowKey === item.rowKey)
            if (index === -1) {
                return
            }

            const nextRow = {
                ...item,
                linkedSubstanceId: selected && selected.id ? selected.id : '',
                linkType: selected && selected.id ? 'manual' : item.linkType,
                linkStatus: selected && selected.id ? 'accepted' : item.linkStatus,
                linkConfidence: selected && selected.id ? null : item.linkConfidence,
                linkReason: selected && selected.id ? null : item.linkReason,
            }

            this.$set(this.localRows, index, nextRow)

            if (selected && selected.id) {
                this.$set(this.selectedSubstancesById, selected.id, selected)
            }
        },
        hydrateLinkedSubstances() {
            const ids = Array.from(new Set(
                this.localRows
                    .map(item => (
                        item && item.linkedSubstanceId ? Number(item.linkedSubstanceId) : null
                    ))
                    .filter(id => Number.isFinite(id) && id > 0),
            ))

            const idsToFetch = ids.filter(id => (
                !this.selectedSubstancesById[id] && !this.hydratingSubstanceIds.includes(id)
            ))

            if (!idsToFetch.length) {
                return
            }

            this.hydratingSubstanceIds = this.hydratingSubstanceIds.concat(idsToFetch)

            SubstanceApi
                .summary(idsToFetch)
                .then(response => {
                    const items = response && response.data && Array.isArray(response.data.items)
                        ? response.data.items
                        : []

                    items.forEach(item => {
                        if (!item || item.id === undefined || item.id === null) {
                            return
                        }

                        this.$set(this.selectedSubstancesById, item.id, {
                            id: item.id,
                            name: item.name || '',
                            title: item.name || '',
                            cas: item.cas || '',
                            ec: item.ec || '',
                            echaId: item.echaId || '',
                            raw: item,
                        })
                    })
                })
                .finally(() => {
                    this.hydratingSubstanceIds = this.hydratingSubstanceIds.filter(id => !idsToFetch.includes(id))
                })
        },
    },
}
</script>

<style scoped>
::v-deep .substances-table table.table th,
::v-deep .substances-table table.table td,
::v-deep [dir] .substances-table table.table th,
::v-deep [dir] .substances-table table.table td {
    padding: 0.45rem 0.6rem !important;
    vertical-align: middle;
}

::v-deep .substances-table .substances-col-match {
    width: 50px;
    min-width: 50px;
}

::v-deep .substances-table .substances-col-substance {
    width: 260px;
    min-width: 260px;
    max-width: 260px;
}

::v-deep .substances-table .substances-col-name {
    width: 240px;
    min-width: 240px;
    max-width: 240px;
}

::v-deep .substances-table .substances-col-ec,
::v-deep .substances-table .substances-col-cas {
    width: 120px;
    min-width: 120px;
}

::v-deep .substances-table .substances-col-operator {
    width: 100px;
    min-width: 100px;
}

::v-deep .substances-table .substances-col-value {
    width: 96px;
    min-width: 96px;
}

::v-deep .substances-table .substances-col-unit {
    width: 130px;
    min-width: 130px;
}

::v-deep .substances-table .substances-col-actions {
    width: 50px;
    min-width: 50px;
}

::v-deep .substances-table .substance-selected-option {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
}

::v-deep .substances-table .vs__selected-options {
    flex-wrap: nowrap;
    overflow: hidden;
}

::v-deep .substances-table .vs__selected {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
}

@media (max-width: 1200px) {
    ::v-deep .substances-table table {
        min-width: 1200px;
    }
}

@media (max-width: 768px) {
    ::v-deep .substances-table table.table th,
    ::v-deep .substances-table table.table td,
    ::v-deep [dir] .substances-table table.table th,
    ::v-deep [dir] .substances-table table.table td {
        padding: 0.38rem 0.45rem !important;
    }
}

.match-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
}

.match-dot-green {
    background: #28c76f;
}

.match-dot-red {
    background: #ea5455;
}
</style>
